/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  Button,
  ButtonGroup,
  IconButton,
  MenuItem,
  MenuList,
  MenuTrigger,
  MoreMenuButton,
  Tooltip,
} from "@microbit/ui";
import React, { ForwardedRef, useCallback, useRef } from "react";
import {
  RiBluetoothLine,
  RiPlayCircleLine,
  RiStopCircleLine,
  RiUsbLine,
} from "react-icons/ri";
import { FormattedMessage, useIntl } from "react-intl";
import { HStack } from "styled-system/jsx";
import { ConnectionStatus } from "@microbit/microbit-connection";
import { useConnectionStatus, useDevice } from "../device/device-hooks";
import { useProjectActions } from "./project-hooks";
import { useHotkeys } from "react-hotkeys-hook";
import {
  globalShortcutConfig,
  keyboardShortcuts,
} from "../common/keyboard-shortcuts";
import { ConnectionAction, FinalFocusRef } from "./project-actions";

/**
 * Groesse der Symbole fuer Start und Stopp.
 *
 * Doppelt so gross wie die Voreinstellung des Knopfs (18 px): die beiden sind
 * die einzigen Knoepfe ohne Beschriftung, und der Stopp muss unter Zeitdruck
 * getroffen werden. Als Groesse am Symbol und nicht als Schriftgroesse am
 * Knopf - die kam dort nicht an.
 */
const SYMBOLGROESSE = "2.25rem";

interface SendButtonProps {
  size?: "lg" | "md" | "sm" | "xs";
  sendButtonRef: React.RefObject<HTMLButtonElement>;
}

const SendButton = React.forwardRef(
  (
    { size, sendButtonRef }: SendButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const status = useConnectionStatus();
    const connected = status === ConnectionStatus.Connected;
    const actions = useProjectActions();
    const device = useDevice();
    // Der Funkweg erscheint nur, wenn der Browser ihn kann. Einen Eintrag
    // anzubieten, der beim Anklicken erklaert, dass er nicht geht, waere
    // schlechter als kein Eintrag.
    const bluetoothPossible =
      typeof navigator !== "undefined" && Boolean(navigator.bluetooth);
    const handleDisconnect = useCallback(async () => {
      await actions.disconnect(menuButtonRef);
    }, [actions]);
    const handleConnectVia = useCallback(
      async (transport: "usb" | "bluetooth") => {
        if (connected) {
          await actions.disconnect(menuButtonRef);
        }
        await actions.connect(
          false,
          ConnectionAction.CONNECT,
          menuButtonRef,
          transport
        );
      },
      [connected, actions]
    );
    // Womit gerade verbunden ist - der Knopf zeigt es, damit niemand raten
    // muss, welche Leitung er gerade benutzt.
    const transportKind =
      (
        device as typeof device & {
          getTransportKind?: () => "usb" | "bluetooth";
        }
      ).getTransportKind?.() ?? "usb";
    const connectedViaBluetooth = connected && transportKind === "bluetooth";
    const intl = useIntl();
    const flashing = useRef<{ flashing: boolean; lastCompleteFlash: number }>({
      flashing: false,
      lastCompleteFlash: 0,
    });
    const handleSendToMicrobit = useCallback(
      async (finalFocusRef: FinalFocusRef) => {
        if (flashing.current.flashing) {
          // Ignore repeated clicks.
          return;
        }
        flashing.current = {
          flashing: true,
          lastCompleteFlash: flashing.current.lastCompleteFlash,
        };
        try {
          await actions.flash(finalFocusRef);
        } finally {
          flashing.current = {
            flashing: false,
            lastCompleteFlash: new Date().getTime(),
          };
        }
      },
      [flashing, actions]
    );
    const handleStop = useCallback(async () => {
      await actions.stop();
    }, [actions]);
    const handleStart = useCallback(async () => {
      await actions.start();
    }, [actions]);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const activeElementRef = useRef<HTMLElement | null>(null);
    const handleSendToMicrobitShortcut = useCallback(() => {
      activeElementRef.current = document.activeElement as HTMLElement;
      handleSendToMicrobit(activeElementRef);
    }, [handleSendToMicrobit]);
    useHotkeys(
      keyboardShortcuts.sendToMicrobit,
      handleSendToMicrobitShortcut,
      globalShortcutConfig
    );
    return (
      <HStack>
        {/*
          Start und Stopp stehen als eigene Knoepfe neben dem Senden und nicht
          im Menue: der Roboter faehrt, und wer ihn anhalten will, hat es
          eilig. Sichtbar werden sie nur bei verbundenem Geraet, damit die
          Leiste sonst so aussieht wie beim micro:bit.

          Nur Symbole, aus Platzgruenden. Die Beschriftung steht deshalb in
          aria-label - ein Knopf ohne Namen ist fuer einen Screenreader kein
          Knopf - und im Tooltip.

          Start uebertraegt nichts. Er startet, was zuletzt auf den Roboter
          gesendet wurde; das sagt der Tooltip, weil es nicht das sein muss,
          was gerade im Editor steht.
        */}
        {connected && (
          <HStack gap="1">
            <Tooltip
              hasArrow
              placement="top start"
              label={intl.formatMessage({ id: "start-hover" })}
            >
              <IconButton
                size={size}
                variant="secondary"
                aria-label={intl.formatMessage({ id: "start-action" })}
                onPress={handleStart}
                data-testid="start-robot"
              >
                <RiPlayCircleLine size={SYMBOLGROESSE} />
              </IconButton>
            </Tooltip>
            <Tooltip
              hasArrow
              placement="top start"
              label={intl.formatMessage({ id: "stop-hover" })}
            >
              <IconButton
                size={size}
                variant="secondary"
                aria-label={intl.formatMessage({ id: "stop-action" })}
                onPress={handleStop}
                data-testid="stop-robot"
              >
                <RiStopCircleLine size={SYMBOLGROESSE} />
              </IconButton>
            </Tooltip>
          </HStack>
        )}
        <ButtonGroup isAttached>
          <Tooltip
            hasArrow
            placement="top start"
            label={intl.formatMessage({
              id: "send-hover",
            })}
          >
            <Button
              ref={ref}
              size={size}
              variant="primary"
              startIcon={
                connectedViaBluetooth ? <RiBluetoothLine /> : <RiUsbLine />
              }
              onPress={() => handleSendToMicrobit(sendButtonRef)}
            >
              <FormattedMessage id="send-action" />
            </Button>
          </Tooltip>
          <MenuTrigger>
            <MoreMenuButton
              ref={menuButtonRef}
              variant="primary"
              aria-label={intl.formatMessage({ id: "more-connect-options" })}
              data-testid="more-connect-options"
              size={size}
            />
            {/*
              Zwei Wege, ein Menue. Der Roboter bietet ueber Kabel und ueber
              Funk dieselbe Konsole an; welchen Weg die Oberflaeche nimmt, ist
              deshalb eine Wahl und keine zweite Betriebsart.
            */}
            <MenuList>
              {connected && (
                <MenuItem
                  icon={connectedViaBluetooth ? <RiBluetoothLine /> : <RiUsbLine />}
                  onAction={handleDisconnect}
                >
                  <FormattedMessage id="disconnect-action" />
                </MenuItem>
              )}
              <MenuItem
                icon={<RiUsbLine />}
                onAction={() => handleConnectVia("usb")}
                data-testid="connect-usb"
              >
                <FormattedMessage id="connect-usb-action" />
              </MenuItem>
              {bluetoothPossible && (
                <MenuItem
                  icon={<RiBluetoothLine />}
                  onAction={() => handleConnectVia("bluetooth")}
                  data-testid="connect-bluetooth"
                >
                  <FormattedMessage id="connect-bluetooth-action" />
                </MenuItem>
              )}
            </MenuList>
          </MenuTrigger>
        </ButtonGroup>
      </HStack>
    );
  }
);

export default SendButton;
